import { computed, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useUserStore } from '@/stores/user.store';
import { buildPageCorpus } from '@/lib/page-corpus';
import { canVisit } from '@/composables/useCanVisit';

// Module level so the sidebar's button and the palette itself share one flag,
// without a store or an event bus for a single boolean.
const isOpen = ref(false);

/**
 * The page search corpus and the role gate it shares with the sidebar.
 *
 * `isPermitted` lives here rather than in `AppSidebar.vue` so the rail and the
 * palette cannot drift: a page hidden from one is necessarily absent from the
 * other. Corpus assembly itself is in `@/lib/page-corpus`, kept free of router
 * and store imports so it stays directly runnable.
 */
export const usePageSearch = () => {
	const route = useRoute();
	const router = useRouter();
	const userStore = useUserStore();

	// Hide items whose target route needs a higher role than the user has.
	const isPermitted = (item: { path: string; hidden?: boolean }) => {
		if (item.hidden) {
			return false;
		}

		const resolved = router.resolve(item.path);

		// A mistyped path in the menu config would otherwise show up as a page
		// that silently lands on not-found. The corpus check can't catch this —
		// it runs without the router — so it is asserted here, where one is.
		if (import.meta.env.DEV && resolved.name === 'not-found') {
			console.warn(`[page search] menu path resolves to not-found: ${item.path}`);
		}

		return canVisit(item.path);
	};

	const groups = computed(() => {
		return buildPageCorpus(isPermitted, route.path);
	});

	const isSignedIn = computed(() => {
		return !!userStore.user;
	});

	return { isOpen, isPermitted, groups, isSignedIn };
};
